import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { UserPlus, Mail, Lock, User, ArrowLeft, RefreshCw } from 'lucide-react';
import LiquidGlassLanguageSelect from '../components/ui/LiquidGlassLanguageSelect';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Register() {
  const [step, setStep] = useState(1); // 1: form, 2: OTP verification
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const { login } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const { getThemedStyles } = useTheme();
  const navigate = useNavigate();
  
  const styles = getThemedStyles();

  // Get browser locale as fallback (e.g., 'pt-BR' -> 'pt', 'en-US' -> 'en')
  const getBrowserLocale = () => {
    const browserLang = navigator.language || navigator.userLanguage || 'en';
    const langCode = browserLang.split('-')[0].toLowerCase();
    // Only return if it's a supported language
    const supportedLangs = ['en', 'pt', 'fr', 'es', 'ru', 'it'];
    return supportedLangs.includes(langCode) ? langCode : 'en';
  };

  // Priority: selected language > browser locale > default 'en'
  const getPreferredLanguage = () => {
    if (language && language !== 'en') return language; // User explicitly selected
    return getBrowserLocale();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error(t('passwordsDoNotMatch'));
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      const preferredLanguage = getPreferredLanguage();
      await axios.post(`${API}/auth/register`, { name, email, password, language: preferredLanguage });
      toast.success('Verification code sent to your email!');
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-registration`, { email, otp });
      const { token, user, is_first_login } = response.data;
      login(token, user, is_first_login);
      toast.success(t('welcome'));
      // Pass isFirstLogin through navigation state for reliable welcome message
      navigate('/dashboard', { state: { isFirstLogin: true } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-otp`, { email });
      toast.success('New verification code sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend code');
    } finally {
      setResending(false);
    }
  };

  const handleBack = () => {
    setStep(1);
    setOtp('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center md:p-4" style={{ backgroundColor: styles.background }}>
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-20 hidden md:block"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1543094152-7f8122e97b66?crop=entropy&cs=srgb&fm=jpg&q=85)' }}
      />
      <div className="absolute inset-0 hidden md:block" style={{ background: `linear-gradient(to top, ${styles.background}, ${styles.background}cc, transparent)` }} />
      
      {/* Language Selector - Top Right (Mobile) */}
      <div className="absolute top-4 right-4 z-10 md:hidden">
        <LiquidGlassLanguageSelect
          value={language}
          onValueChange={setLanguage}
          testId="language-select-mobile"
          options={[
            { code: 'en', label: t('english') },
            { code: 'pt', label: t('portuguese') },
            { code: 'fr', label: t('french') },
            { code: 'es', label: t('spanish') },
            { code: 'el', label: t('greek') },
            { code: 'ru', label: t('russian') },
            { code: 'it', label: t('italian') },
          ]}
        />
      </div>
      
      <div className="relative w-full max-w-lg">
        <div className="min-h-screen md:min-h-0 md:rounded-2xl p-6 md:p-8 md:border flex flex-col justify-center" style={{ backgroundColor: styles.surface, borderColor: styles.secondary }}>
          {/* Language Selector - Inside card (Desktop) */}
          <div className="hidden md:flex justify-end mb-4">
            <LiquidGlassLanguageSelect
              value={language}
              onValueChange={setLanguage}
              options={[
                { code: 'en', label: t('english') },
                { code: 'pt', label: t('portuguese') },
                { code: 'fr', label: t('french') },
                { code: 'es', label: t('spanish') },
                { code: 'el', label: t('greek') },
                { code: 'ru', label: t('russian') },
                { code: 'it', label: t('italian') },
              ]}
            />
          </div>
          
          {step === 1 ? (
            <>
              <div className="text-center mb-8 md:mb-10">
                <h1 className="font-heading text-3xl md:text-4xl font-bold mb-2" style={{ color: styles.primary, letterSpacing: '2px' }}>Join Sitoa</h1>
                <p className="text-sm md:text-base italic" style={{ color: styles.textMuted }}>from yes to welcome</p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-base" style={{ color: styles.textMuted }}>{t('name')}</Label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: styles.textMuted }} />
                    <Input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="pl-12 py-6 text-base rounded-xl"
                      style={{ backgroundColor: styles.mode === 'light' ? '#ffffff' : styles.secondary, borderColor: styles.secondary, color: styles.text }}
                      placeholder="Your name"
                      required
                      data-testid="register-name-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-base" style={{ color: styles.textMuted }}>{t('email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: styles.textMuted }} />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-12 py-6 text-base rounded-xl"
                      style={{ backgroundColor: styles.mode === 'light' ? '#ffffff' : styles.secondary, borderColor: styles.secondary, color: styles.text }}
                      placeholder="you@example.com"
                      required
                      data-testid="register-email-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-base" style={{ color: styles.textMuted }}>{t('password')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: styles.textMuted }} />
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-12 py-6 text-base rounded-xl"
                      style={{ backgroundColor: styles.mode === 'light' ? '#ffffff' : styles.secondary, borderColor: styles.secondary, color: styles.text }}
                      placeholder="••••••••"
                      required
                      minLength={6}
                      data-testid="register-password-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-base" style={{ color: styles.textMuted }}>{t('confirmPassword')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: styles.textMuted }} />
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-12 py-6 text-base rounded-xl"
                      style={{ backgroundColor: styles.mode === 'light' ? '#ffffff' : styles.secondary, borderColor: styles.secondary, color: styles.text }}
                      placeholder="••••••••"
                      required
                      data-testid="register-confirm-password-input"
                    />
                  </div>
                </div>
                
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full font-semibold rounded-xl py-6 text-base md:text-lg transition-all duration-300"
                  style={{ backgroundColor: styles.primary, color: styles.mode === 'light' ? '#ffffff' : styles.background }}
                  data-testid="register-submit-btn"
                >
                  <UserPlus className="mr-2 h-5 w-5" />
                  {loading ? 'Sending code...' : t('register')}
                </Button>

                {/* Google Register - HIDDEN: Temporarily disabled pending custom OAuth setup */}
                {false && <>
                {/* Divider */}
                <div className="relative my-2">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t" style={{ borderColor: styles.secondary }} />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="px-3" style={{ backgroundColor: styles.mode === 'light' ? '#ffffff' : styles.card, color: styles.textMuted }}>{t('or') || 'or'}</span>
                  </div>
                </div>

                {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full font-semibold rounded-xl py-6 text-base transition-all duration-300 flex items-center justify-center gap-3"
                  style={{ borderColor: styles.secondary, color: styles.primary, backgroundColor: styles.mode === 'light' ? '#ffffff' : styles.secondary }}
                  onClick={() => {
                    const redirectUrl = window.location.origin + '/dashboard';
                    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                  }}
                  data-testid="google-register-btn"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  {t('signInWithGoogle') || 'Sign in with Google'}
                </Button>
                </>}
              </form>
              
              <p className="text-center mt-6 text-base" style={{ color: styles.textMuted }}>
                {t('alreadyHaveAccount')}{' '}
                <Link to="/login" className="hover:underline" style={{ color: styles.primary }} data-testid="login-link">
                  {t('login')}
                </Link>
              </p>
            </>
          ) : (
            <>
              <button
                onClick={handleBack}
                className="flex items-center mb-4 transition-colors text-base"
                style={{ color: styles.textMuted }}
              >
                <ArrowLeft className="h-5 w-5 mr-1" />
                Back
              </button>
              
              <div className="text-center mb-8 md:mb-10">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: `${styles.primary}33` }}>
                  <Mail className="h-10 w-10" style={{ color: styles.primary }} />
                </div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold mb-2" style={{ color: styles.primary }}>Verify Your Email</h1>
                <p className="text-base" style={{ color: styles.textMuted }}>
                  We sent a 6-digit code to<br />
                  <span className="font-medium" style={{ color: styles.text }}>{email}</span>
                </p>
              </div>
              
              <form onSubmit={handleVerifyOTP} className="space-y-5 md:space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="otp" className="text-base" style={{ color: styles.textMuted }}>Verification Code</Label>
                  <Input
                    id="otp"
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="text-center text-2xl md:text-3xl tracking-[0.5em] font-mono py-6 rounded-xl"
                    style={{ backgroundColor: styles.mode === 'light' ? '#ffffff' : styles.secondary, borderColor: styles.secondary, color: styles.text }}
                    placeholder="000000"
                    maxLength={6}
                    required
                    data-testid="otp-input"
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  className="w-full font-semibold rounded-xl py-6 text-base md:text-lg transition-all duration-300"
                  style={{ backgroundColor: styles.primary, color: styles.mode === 'light' ? '#ffffff' : styles.background }}
                  data-testid="verify-otp-btn"
                >
                  {loading ? 'Verifying...' : 'Verify & Create Account'}
                </Button>
              </form>
              
              <div className="text-center mt-6">
                <p className="text-base mb-2" style={{ color: styles.textMuted }}>Did not receive the code?</p>
                <button
                  onClick={handleResendOTP}
                  disabled={resending}
                  className="hover:underline flex items-center justify-center mx-auto text-base"
                  style={{ color: styles.primary }}
                >
                  <RefreshCw className={`h-5 w-5 mr-1 ${resending ? 'animate-spin' : ''}`} />
                  {resending ? 'Sending...' : 'Resend Code'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
